<template>
	<div
		ref="clickOutsideRef"
		class="kk-select-layout"
	>
		<div ref="buttonRef">
			<slot
				name="button"
				v-bind="scopedSlots"
			/>
		</div>
		<div
			ref="menuRef"
			class="kk-select-layout__menu"
		>
			<div v-show="isMenuOpen">
				<slot
					name="menu"
					v-bind="scopedSlots"
				/>
			</div>
		</div>
	</div>
</template>

<script>
import { nextTick, onMounted, onUnmounted, ref, watch } from 'vue';
import { onClickOutside } from '@vueuse/core';
import { createPopper } from '@popperjs/core';
import { isFunction } from 'lodash-es';

export const PLACEMENT = ['bottom-start', 'bottom-end'];
export default {
	name: 'KkSelectLayout',
	props: {
		menuOffset: {
			type: Array,
			default: () => [0, 4], // https://popper.js.org/docs/v2/modifiers/offset/
		},
		isOpen: {
			type: Boolean,
			default: false,
		},
		clickOutsideHandler: {
			type: Function,
			default: null
		},
		placement: {
			type: String,
			default: PLACEMENT[0],
			validator: item => PLACEMENT.includes(item),
		}
	},
	setup(props) {
		const isMenuOpen = ref(false);
		const clickOutsideRef = ref(null);
		onClickOutside(clickOutsideRef, handleClickOutside);

		const popperInstance = ref(null);
		const buttonRef = ref(null);
		const menuRef = ref(null);

		watch(
			() => props.isOpen,
			(value) => {
				isMenuOpen.value = value
			},
			{immediate: true},
		)

		const initializePopper = () => {
			popperInstance.value = createPopper(buttonRef.value, menuRef.value, {
				placement: props.placement,
				modifiers: [
					{
						name: 'offset',
						options: {
							offset: props.menuOffset,
						}
					}
				]
			})
		}
		const updatePopper = () => {
			popperInstance.value?.update();
		}
		const destroyPopper = () => {
			popperInstance.value?.destroy();
		}

		onMounted(() => {
			nextTick(() => initializePopper());
		})
		onUnmounted(() => {
			destroyPopper();
		})

		function handleClickOutside() {
			if (isFunction(props.clickOutsideHandler)) {
				props.clickOutsideHandler();
				return;
			}
			closeMenu();
		}

		function openMenu() {
			isMenuOpen.value = true;
			nextTick(() => {
				updatePopper();
			});
		}

		function closeMenu() {
			isMenuOpen.value = false;
		}

		function toggleMenu() {
			if (isMenuOpen.value) {
				closeMenu();
			} else {
				openMenu();
			}
		}

		return {
			clickOutsideRef,
			buttonRef,
			menuRef,
			isMenuOpen,
			openMenu,
			closeMenu,
			toggleMenu
		};
	},
	computed: {
		scopedSlots() {
			return {
				isMenuOpen: this.isMenuOpen,
				openMenu: this.openMenu,
				closeMenu: this.closeMenu,
				toggleMenu: this.toggleMenu,
			};
		},
	},
	watch: {
		isMenuOpen(isMenuOpen) {
			const eventName = isMenuOpen ? 'open:menu' : 'close:menu';

			this.$emit(eventName);
		},
	},
};
</script>
